/**
 * Session context super properties: attached to every PostHog event so any
 * insight can be broken down by how the app is running.
 *
 *   - `display_mode`     'pwa' when launched from the home screen, else 'browser'
 *   - `device_os`        'ios' | 'android' | 'macos' | 'windows' | 'chromeos' | 'linux' | 'other'
 *   - `push_permission`  browser Notification permission, or 'unsupported'
 *   - `push_subscribed`  OneSignal subscription state; signed-in sessions only,
 *                        because OneSignal reports it only once a user is set
 *
 * `device_os` exists next to PostHog's own `$os` because `$os` reads iPadOS
 * as "Mac OS X", which hides iPad users from every iOS push/PWA breakdown.
 */

import { register, unregister } from './posthog';
import { isIOSDevice, isStandalonePWA } from '@/utils/platform';

export type DisplayMode = 'pwa' | 'browser';
export type DeviceOs = 'ios' | 'android' | 'macos' | 'windows' | 'chromeos' | 'linux' | 'other';
export type PushPermission = NotificationPermission | 'unsupported';

export type PushState = {
  isSupported: boolean;
  isInitialized: boolean;
  isSubscribed: boolean;
  browserPermission: NotificationPermission;
};

export function detectDeviceOs(userAgent: string, isIOS: boolean): DeviceOs {
  if (isIOS) return 'ios';
  if (/android/i.test(userAgent)) return 'android';
  if (/windows/i.test(userAgent)) return 'windows';
  if (/cros/i.test(userAgent)) return 'chromeos';
  if (/macintosh|mac os x/i.test(userAgent)) return 'macos';
  if (/linux/i.test(userAgent)) return 'linux';
  return 'other';
}

export function readPushPermission(): PushPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export function pushStateProperties(state: PushState): Record<string, unknown> {
  const properties: Record<string, unknown> = {
    push_permission: state.isSupported ? state.browserPermission : 'unsupported',
  };
  // Before OneSignal initializes `isSubscribed` is a default `false`, not a
  // fact; keep the last known value instead of overwriting it.
  if (state.isInitialized) properties.push_subscribed = state.isSubscribed;
  return properties;
}

// Last push properties seen this page load, re-applied after `reset()`
// wipes the persisted super properties on sign-out.
let lastPushProperties: Record<string, unknown> = {};

/** Call on boot after `initPostHog`, and again after every PostHog `reset()`. */
export function registerSessionContext(): void {
  if (typeof navigator === 'undefined') return;
  const displayMode: DisplayMode = isStandalonePWA() ? 'pwa' : 'browser';
  // PostHog persists super properties across page loads; a subscription
  // state from a previous load is not a fact about this one.
  if (!('push_subscribed' in lastPushProperties)) unregister('push_subscribed');
  register({
    display_mode: displayMode,
    device_os: detectDeviceOs(navigator.userAgent, isIOSDevice()),
    push_permission: readPushPermission(),
    ...lastPushProperties,
  });
}

/** Wire to `OneSignalService.onSubscriptionChange`. */
export function registerPushState(state: PushState): void {
  const next = { ...lastPushProperties, ...pushStateProperties(state) };
  // OneSignal's 60s poll notifies even when nothing changed.
  if (
    next.push_permission === lastPushProperties.push_permission &&
    next.push_subscribed === lastPushProperties.push_subscribed
  ) return;
  lastPushProperties = next;
  register(lastPushProperties);
}
